import { Card, Row, Col, Statistic, Progress, Tag } from 'antd';
import { DashboardOutlined, DatabaseOutlined, OrderedListOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import type { MemoryStats } from '../types';
import { formatRelativeTime } from '../utils/format';

export default function Monitor() {
  const [stats, setStats] = useState<MemoryStats | null>(null);
  const [sampleTimeDisplay, setSampleTimeDisplay] = useState<string>('');
  const intervalRef = useRef<number | null>(null);
  const timeUpdateRef = useRef<number | null>(null);

  const fetchStats = async () => {
    try {
      const res = await api.get('stats/memory').json<{ success: boolean; data: MemoryStats }>();
      if (res.success && res.data) {
        setStats(res.data);
        updateSampleTimeDisplay(res.data.sampledAt);
      }
    } catch (error) {
      console.error('Failed to fetch memory stats:', error);
    }
  };

  const updateSampleTimeDisplay = (sampledAt: string) => {
    setSampleTimeDisplay(formatRelativeTime(sampledAt));
  };

  useEffect(() => {
    // 立即获取一次
    fetchStats();

    // 每秒刷新数据
    intervalRef.current = setInterval(fetchStats, 1000);

    // 每秒更新采样时间显示
    timeUpdateRef.current = setInterval(() => {
      if (stats?.sampledAt) {
        updateSampleTimeDisplay(stats.sampledAt);
      }
    }, 1000);

    // 清理定时器
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeUpdateRef.current) {
        clearInterval(timeUpdateRef.current);
        timeUpdateRef.current = null;
      }
    };
  }, []);

  // 更新采样时间显示（当 stats 变化时）
  useEffect(() => {
    if (stats?.sampledAt) {
      updateSampleTimeDisplay(stats.sampledAt);
    }
  }, [stats?.sampledAt]);

  const heapUsagePercent = stats
    ? Math.round((stats.process.heapUsed / stats.process.heapTotal) * 100)
    : 0;

  // 格式化字节数（用于进程内存）
  const formatProcessBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>
        系统监控
        <Tag color="green" style={{ marginLeft: 8 }}>自动刷新: 1s</Tag>
        {stats?.sampledAt && (
          <Tag color="blue" style={{ marginLeft: 8 }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            采样时间: {sampleTimeDisplay}
          </Tag>
        )}
      </h2>

      {/* 进程内存 */}
      <Card
        title={
          <span>
            <DashboardOutlined style={{ marginRight: 8 }} />
            进程内存 (process.memoryUsage)
          </span>
        }
        style={{ marginBottom: 24 }}
      >
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="RSS (物理内存)"
              value={formatProcessBytes(stats?.process.rss ?? 0)}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Heap Total (堆总内存)"
              value={formatProcessBytes(stats?.process.heapTotal ?? 0)}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Heap Used (堆已用内存)"
              value={formatProcessBytes(stats?.process.heapUsed ?? 0)}
              valueStyle={{ color: heapUsagePercent > 80 ? '#cf1322' : '#3f8600' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="External (外部内存)"
              value={formatProcessBytes(stats?.process.external ?? 0)}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <span style={{ marginRight: 8 }}>Heap 使用率:</span>
          <Progress
            percent={heapUsagePercent}
            status={heapUsagePercent > 80 ? 'exception' : 'active'}
            style={{ maxWidth: 400, display: 'inline-block' }}
          />
        </div>
      </Card>

      {/* 缓存和队列 */}
      <Row gutter={16}>
        <Col span={12}>
          <Card
            title={
              <span>
                <DatabaseOutlined style={{ marginRight: 8 }} />
                缓存占用
                <Tag color={stats?.cache.type === 'redis' ? 'blue' : 'orange'} style={{ marginLeft: 8 }}>
                  {stats?.cache.type ?? 'memory'}
                </Tag>
              </span>
            }
          >
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="条目数"
                  value={stats?.cache.keys ?? 0}
                  suffix="条"
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title={
                    <span>
                      内存占用
                      <Tag color="purple" style={{ marginLeft: 4, fontSize: 10 }}>采样</Tag>
                    </span>
                  }
                  value={stats?.cache.bytesHuman ?? '0 B'}
                />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={8}>
                <Statistic
                  title="命中率"
                  value={(stats?.cache.hitRate ?? 0) * 100}
                  precision={1}
                  suffix="%"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="命中"
                  value={stats?.cache.hits ?? 0}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="未命中"
                  value={stats?.cache.misses ?? 0}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16, color: '#888', fontSize: 12 }}>
              {stats?.cache.type === 'memory' && (
                <span>精确计算 (object-sizeof)</span>
              )}
              {stats?.cache.type === 'redis' && (
                <span>精确计算 (MEMORY USAGE)</span>
              )}
            </div>
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <span>
                <OrderedListOutlined style={{ marginRight: 8 }} />
                日志队列
                <Tag color={stats?.logQueue.type === 'redis' ? 'blue' : 'orange'} style={{ marginLeft: 8 }}>
                  {stats?.logQueue.type ?? 'memory'}
                </Tag>
              </span>
            }
          >
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="队列长度"
                  value={stats?.logQueue.length ?? 0}
                  suffix="条"
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title={
                    <span>
                      内存占用
                      <Tag color="purple" style={{ marginLeft: 4, fontSize: 10 }}>采样</Tag>
                    </span>
                  }
                  value={stats?.logQueue.bytesHuman ?? '0 B'}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16, color: '#888', fontSize: 12 }}>
              {stats?.logQueue.type === 'memory' && (
                <span>精确计算 (object-sizeof)</span>
              )}
              {stats?.logQueue.type === 'redis' && (
                <span>精确计算 (MEMORY USAGE)</span>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
